import React from "react";
import {
    VStack,
    Heading,
    Text,
    Box,
    Container,
    Icon,
    Flex,
    Divider,
    Badge,
    Link,
    useColorModeValue,
    UnorderedList,
    ListItem,
    Button,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import {
    FaShieldAlt,
    FaUserLock,
    FaBan,
    FaWallet,
    FaEnvelope,
    FaHandshake,
    FaExclamationTriangle,
    FaGavel,
    FaPowerOff,
    FaLock,
    FaArrowLeft,
} from "react-icons/fa";
import { MdOutlineDescription, MdVerified, MdUpdate } from "react-icons/md";
import { BiWorld } from "react-icons/bi";

const Section = ({ icon, title, children, accentColor, cardBg, textColor, borderColor }) => (
    <Box
        w="100%"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        rounded="xl"
        p={6}
        shadow="sm"
        transition="all 0.2s"
        _hover={{ shadow: "md" }}
    >
        <Flex align="center" mb={4} gap={3}>
            <Flex
                align="center"
                justify="center"
                bg={useColorModeValue("brand.50", "brand.900")}
                p={2}
                rounded="lg"
                color={accentColor}
                flexShrink={0}
            >
                <Icon as={icon} boxSize={5} />
            </Flex>
            <Heading fontSize={{ base: "md", md: "lg" }} fontWeight="700" color={textColor}>
                {title}
            </Heading>
        </Flex>
        <Box color={useColorModeValue("gray.600", "gray.400")} fontSize="md" lineHeight="tall">
            {children}
        </Box>
    </Box>
);

const Terms = () => {
    const navigate = useNavigate();
    const bg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.800", "white");
    const subTextColor = useColorModeValue("gray.600", "gray.400");
    const accentColor = useColorModeValue("brand.500", "brand.200");
    const borderColor = useColorModeValue("gray.200", "gray.700");
    const warningBg = useColorModeValue("orange.50", "orange.900");
    const warningBorder = useColorModeValue("orange.200", "orange.700");
    const warningText = useColorModeValue("orange.700", "orange.200");
    const dangerBg = useColorModeValue("red.50", "red.900");
    const dangerBorder = useColorModeValue("red.200", "red.700");
    const dangerText = useColorModeValue("red.700", "red.200");

    const sectionProps = { accentColor, cardBg, textColor, borderColor };

    return (
        <>
            <Helmet>
                <title>Terms of Service | Techy India</title>
                <meta
                    name="description"
                    content="Read the Techyindia Terms of Service to understand your rights and responsibilities when using our platform."
                />
            </Helmet>

            <VStack
                w="100%"
                minH="100vh"
                bg={bg}
                spacing={0}
                align="stretch"
                py={8}
            >
                <Container maxW="container.lg">
                    <VStack spacing={8} align="center" w="100%">

                        {/* Back Button */}
                        <Flex w="100%" justify="flex-start">
                            <Button
                                leftIcon={<Icon as={FaArrowLeft} />}
                                onClick={() => navigate(-1)}
                                variant="ghost"
                                size="sm"
                                color={accentColor}
                                _hover={{ bg: useColorModeValue("brand.50", "brand.900") }}
                            >
                                Go Back
                            </Button>
                        </Flex>
                        {/* Header */}
                        <VStack spacing={4} textAlign="center">
                            <Flex
                                align="center"
                                justify="center"
                                bg={useColorModeValue("brand.100", "brand.900")}
                                p={4}
                                rounded="full"
                                color="brand.500"
                            >
                                <Icon as={FaGavel} boxSize={7} />
                            </Flex>
                            <Heading
                                fontSize={{ base: "2xl", md: "4xl" }}
                                fontWeight="800"
                                color={textColor}
                                letterSpacing="-0.5px"
                            >
                                Terms of{" "}
                                <Text as="span" color="brand.500">
                                    Service
                                </Text>
                            </Heading>
                            <Text color={subTextColor} fontSize="md" maxW="600px">
                                By accessing or using Techyindia, you agree to be bound by these
                                Terms of Service. Please read them carefully.
                            </Text>
                            <Badge colorScheme="brand" fontSize="sm" px={3} py={1} rounded="full">
                                Last Updated: March 15, 2026
                            </Badge>
                        </VStack>

                        <Divider borderColor={borderColor} />

                        {/* Section 1: Acceptance of Terms */}
                        <Section icon={MdVerified} title="1. Acceptance of Terms" {...sectionProps}>
                            <Text mb={2}>By using Techyindia, you confirm that:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>You are at least <Text as="span" fontWeight="700" color={textColor}>18 years old</Text></ListItem>
                                <ListItem>You agree to comply with all applicable laws and regulations</ListItem>
                                <ListItem>You accept these Terms and our Privacy Policy</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 2: Description of Service */}
                        <Section icon={MdOutlineDescription} title="2. Description of Service" {...sectionProps}>
                            <Text mb={2}>Techyindia provides:</Text>
                            <UnorderedList spacing={1} pl={2} mb={4}>
                                <ListItem>Temporary virtual phone numbers</ListItem>
                                <ListItem>SMS receiving services</ListItem>
                                <ListItem>Wallet-based balance system for service usage</ListItem>
                            </UnorderedList>
                            <Text mb={2}>These services are intended for:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Testing purposes</ListItem>
                                <ListItem>Development and legitimate verification use cases</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 3: Prohibited Activities */}
                        <Section icon={FaBan} title="3. Prohibited Activities" {...sectionProps}>
                            <Text mb={2}>Users are strictly prohibited from:</Text>
                            <UnorderedList spacing={1} pl={2} mb={4}>
                                <ListItem>Using the service for illegal or fraudulent purposes</ListItem>
                                <ListItem>Bypassing security or verification systems of third-party platforms</ListItem>
                                <ListItem>Engaging in spam, abuse, or harassment</ListItem>
                                <ListItem>Violating terms of other platforms using our services</ListItem>
                            </UnorderedList>
                            <Box
                                bg={dangerBg}
                                border="1px solid"
                                borderColor={dangerBorder}
                                rounded="lg"
                                p={4}
                            >
                                <Text color={dangerText} fontWeight="700" mb={2}>
                                    🚫 Any such activity may result in:
                                </Text>
                                <UnorderedList spacing={1} pl={2} color={dangerText} fontWeight="600">
                                    <ListItem>Immediate account suspension</ListItem>
                                    <ListItem>Permanent ban without notice</ListItem>
                                </UnorderedList>
                            </Box>
                        </Section>

                        {/* Section 4: Account Responsibility */}
                        <Section icon={FaUserLock} title="4. Account Responsibility" {...sectionProps}>
                            <Text mb={2}>Users are responsible for:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Maintaining the confidentiality of their account</ListItem>
                                <ListItem>All activities performed under their account</ListItem>
                                <ListItem>Providing accurate and truthful information</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 5: Payments and Wallet */}
                        <Section icon={FaWallet} title="5. Payments and Wallet" {...sectionProps}>
                            <Text mb={3}>Techyindia operates a <Text as="span" fontWeight="700" color={textColor}>prepaid wallet system</Text>.</Text>
                            <Text mb={3} fontWeight="600" color={accentColor}>
                                Payments are processed through external payment providers and are not handled by Google Play.
                            </Text>
                            <Text fontWeight="600" color={textColor} mb={2}>Important:</Text>
                            <UnorderedList spacing={1} pl={2} mb={4}>
                                <ListItem>Payments are made externally (e.g., QR-based payment systems)</ListItem>
                                <ListItem>Users must submit valid transaction IDs (TXID) for verification</ListItem>
                                <ListItem>Balance is credited only after successful verification</ListItem>
                            </UnorderedList>
                            <Box
                                bg={warningBg}
                                border="1px solid"
                                borderColor={warningBorder}
                                rounded="lg"
                                p={4}
                            >
                                <Text color={warningText} fontWeight="700" mb={2}>
                                    ⚠️ We are not responsible for:
                                </Text>
                                <UnorderedList spacing={1} pl={2} color={warningText}>
                                    <ListItem>Incorrect TXID submissions</ListItem>
                                    <ListItem>Payment sent to wrong QR/account</ListItem>
                                    <ListItem>Delays due to verification processes</ListItem>
                                </UnorderedList>
                            </Box>
                        </Section>

                        {/* Section 6: No Guarantee of Availability */}
                        <Section icon={FaExclamationTriangle} title="6. No Guarantee of Availability" {...sectionProps}>
                            <Text mb={2}>We do not guarantee:</Text>
                            <UnorderedList spacing={1} pl={2} mb={3}>
                                <ListItem>Continuous availability of specific phone numbers</ListItem>
                                <ListItem>Delivery of SMS for all services</ListItem>
                                <ListItem>Compatibility with all third-party platforms</ListItem>
                            </UnorderedList>
                            <Text fontSize="sm" fontStyle="italic">
                                Service availability may vary based on external providers.
                            </Text>
                        </Section>

                        {/* Section 7: Suspension and Termination */}
                        <Section icon={FaPowerOff} title="7. Suspension and Termination" {...sectionProps}>
                            <Text mb={2}>We reserve the right to:</Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Suspend or terminate accounts at our discretion</ListItem>
                                <ListItem>Remove access without prior notice for policy violations</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 8: Limitation of Liability */}
                        <Section icon={FaShieldAlt} title="8. Limitation of Liability" {...sectionProps}>
                            <Text mb={2}>Techyindia shall not be liable for:</Text>
                            <UnorderedList spacing={1} pl={2} mb={3}>
                                <ListItem>Loss of data</ListItem>
                                <ListItem>Financial loss</ListItem>
                                <ListItem>Service interruptions</ListItem>
                                <ListItem>Third-party service failures</ListItem>
                            </UnorderedList>
                            <Text fontSize="sm" fontStyle="italic">
                                Use of the service is at your own risk.
                            </Text>
                        </Section>

                        {/* Section 9: Third-Party Services */}
                        <Section icon={BiWorld} title="9. Third-Party Services" {...sectionProps}>
                            <Text mb={2}>
                                Our app may interact with third-party platforms.
                                We are not responsible for their:
                            </Text>
                            <UnorderedList spacing={1} pl={2}>
                                <ListItem>Policies</ListItem>
                                <ListItem>Service changes</ListItem>
                                <ListItem>Restrictions</ListItem>
                            </UnorderedList>
                        </Section>

                        {/* Section 10: Intellectual Property */}
                        <Section icon={FaLock} title="10. Intellectual Property" {...sectionProps}>
                            <Text>
                                All content, branding, and services of Techyindia are{" "}
                                <Text as="span" fontWeight="700" color={textColor}>owned by us</Text>.
                                Unauthorized use is prohibited.
                            </Text>
                        </Section>

                        {/* Section 11: Changes to Terms */}
                        <Section icon={MdUpdate} title="11. Changes to Terms" {...sectionProps}>
                            <Text>
                                We may update these Terms at any time. Continued use of the service
                                implies acceptance of updated Terms.
                            </Text>
                        </Section>

                        {/* Section 12: Contact */}
                        <Section icon={FaEnvelope} title="12. Contact Information" {...sectionProps}>
                            <Text mb={2}>For support or inquiries:</Text>
                            <Flex align="center" gap={2}>
                                <Icon as={FaEnvelope} color={accentColor} />
                                <Link
                                    href="mailto:support@techyindia.app"
                                    color={accentColor}
                                    fontWeight="600"
                                    _hover={{ textDecoration: "underline" }}
                                >
                                    support@techyindia.app
                                </Link>
                            </Flex>
                        </Section>

                        {/* Section 13: Governing Law */}
                        <Section icon={FaGavel} title="13. Governing Law" {...sectionProps}>
                            <Text>
                                These Terms shall be governed by and interpreted under applicable
                                laws of your jurisdiction.
                            </Text>
                        </Section>

                        {/* Section 14: Consent */}
                        <Box
                            w="100%"
                            bg={useColorModeValue("brand.50", "brand.900")}
                            border="1px solid"
                            borderColor={useColorModeValue("brand.200", "brand.700")}
                            rounded="xl"
                            p={6}
                            textAlign="center"
                        >
                            <Icon as={FaHandshake} boxSize={6} color={accentColor} mb={3} />
                            <Heading fontSize="md" fontWeight="700" color={textColor} mb={2}>
                                14. Consent
                            </Heading>
                            <Text color={subTextColor} fontSize="md">
                                By using Techyindia, you acknowledge that you have read, understood,
                                and agreed to these Terms of Service.
                            </Text>
                        </Box>

                    </VStack>
                </Container>
            </VStack>
        </>
    );
};

export default Terms;
